import {
    createInitialResourceState,
    createResourceReducer,
} from "@/hooks/reducers/resource.reducer";
import { parseApiError } from "@/utils/errorHandler";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useDebounce } from "@/hooks/useDebounce";

interface ApiService<T, D> {
    getAll: (filters: any) => Promise<{ results: T[]; count: number }>;
    create: (data: D) => Promise<T>;
    update: (id: number | string, data: Partial<D>) => Promise<T>;
    remove: (id: number | string) => Promise<void>;
}

export const usePaginatedResource = <T, F, D>(
    apiService: ApiService<T, D>,
    defaultFilters: F,
    debounceDelay: number = 500,
) => {
    const resourceReducer = createResourceReducer<T, F>();
    const [state, dispatch] = useReducer(
        resourceReducer,
        createInitialResourceState(defaultFilters),
    );

    const [localFilters, setLocalFilters] = useState<F>(defaultFilters);

    const debouncedFilters = useDebounce(localFilters, debounceDelay);

    const fetchResource = useCallback(async () => {
        dispatch({ type: "FETCH_START" });
        const fetchFilters: Record<string, any> = {
            page: state.currentPage.toString(),
        };

        for (const key in state.filters) {
            const value = state.filters[key];
            if (value !== null && value !== undefined && value !== "") {
                fetchFilters[key] = String(value);
            }
        }
        try {
            const payload = await apiService.getAll({
                page: state.currentPage,
                ...state.filters,
            });
            dispatch({ type: "FETCH_SUCCESS" });
            dispatch({
                type: "SET_DATA",
                payload: { results: payload.results, count: payload.count },
            });
        } catch (err) {
            dispatch({ type: "FETCH_FAILURE", payload: parseApiError(err) });
        }
    }, [apiService, state.currentPage, state.filters]);

    useEffect(() => {
        if (JSON.stringify(debouncedFilters) !== JSON.stringify(state.filters)) {
            dispatch({ type: "SET_FILTERS", payload: debouncedFilters });
        }
    }, [debouncedFilters, state.filters]);

    useEffect(() => {
        fetchResource();
    }, [fetchResource]);

    const performMutation = useCallback(
        async (mutationFn: () => Promise<any>): Promise<any> => {
            dispatch({ type: "SUBMIT_START" });
            try {
                const result = await mutationFn();
                await fetchResource();
                dispatch({ type: "SUBMIT_SUCCESS" });
                return result;
            } catch (error) {
                const parsedError = parseApiError(error);
                dispatch({ type: "SUBMIT_FAILURE", payload: parsedError });
                throw parsedError;
            }
        },
        [fetchResource],
    );

    const create = useCallback(
        (data: D): Promise<T> => performMutation(() => apiService.create(data)),
        [apiService, performMutation],
    );

    const update = useCallback(
        (id: number | string, data: Partial<D>): Promise<T> =>
            performMutation(() => apiService.update(id, data)),
        [apiService, performMutation],
    );

    const remove = useCallback(
        (id: number | string): Promise<void> =>
            performMutation(() => apiService.remove(id)),
        [apiService, performMutation],
    );

    const setPage = useCallback((page: number) => {
        dispatch({ type: "SET_PAGE", payload: page });
    }, []);

    const setFilters = useCallback((filters: Partial<F>) => {
        setLocalFilters((prevFilters) => ({ ...prevFilters, ...filters }));
    }, []);

    const resetError = useCallback(() => {
        dispatch({ type: "RESET_ERROR" });
    }, []);

    return {
        ...state,
        filters: localFilters,
        setPage,
        setFilters,
        create,
        update,
        remove,
        resetError,
    };
};
